import { Suspense } from 'react'

import { createFileRoute } from '@tanstack/react-router'

import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import { AddLogSourceSkeleton } from '@/features/integrations/collectors/components/skeletons'
import { collectorQueries } from '@/features/integrations/collectors/queries'
import { lazyNamed } from '@/utils/lazy-named'

import { ROLES } from '@/shared/auth/constants'
import { getCurrentTenant } from '@/shared/auth/store'
import { roleAuthGuard } from '@/shared/auth/utils/router'

import { ErrorBoundary } from '@/components/common/error-boundary'

/**
 * Loaded on intent, not eagerly (P-21): the form is reached only by clicking a
 * Network Logs catalog card, which preloads the chunk on hover.
 *
 * The Collector and the device type are path segments, not state the form
 * carries: a Log Source cannot exist without either, and the create request
 * needs both ids — so a refresh or a link shared with a teammate has to land on
 * the same form. There is no redirect from the older collector-less path;
 * nothing ever held a link to it.
 */
const AddLogSourceWizard = lazyNamed(
	() =>
		import('@/features/integrations/collectors/components/add-log-source/wizard'),
	'AddLogSourceWizard',
)

export const Route = createFileRoute(
	'/_dashboard/integrations/collectors/$collectorId/sources/new/$definitionId',
)({
	// The same bar `/integrations/new` enforces and the card that links here sits
	// behind (D34): a gate on the affordance alone leaves the URL open.
	beforeLoad: () => roleAuthGuard(ROLES.SystemManager),

	// Every read the form suspends on (A-07), so a no-plan tenant's upsell does
	// not wait behind a waterfall. The options are both the plan check and the
	// device picker. The Collector comes from the fleet list rather than a by-id
	// read because the select needs every Collector anyway, and its embedded
	// listeners are what the port-conflict check reads.
	loader: ({ context: { queryClient } }) => {
		const tenant = getCurrentTenant()
		if (!tenant) return

		queryClient.prefetchQuery(collectorQueries.logSourceOptions(tenant))
		queryClient.prefetchQuery(collectorQueries.summaryList(tenant))
	},

	head: () => ({
		meta: [{ title: 'Add Log Source | Integrations | BitLyft AIR®' }],
	}),

	component: AddLogSourcePage,
	pendingComponent: AddLogSourceSkeleton,
	errorComponent: RouteErrorComponent,
})

function AddLogSourcePage() {
	const { collectorId, definitionId } = Route.useParams()

	return (
		<ErrorBoundary
			feature="integrations"
			section="add-log-source"
			entityId={definitionId}
		>
			<Suspense fallback={<AddLogSourceSkeleton />}>
				<AddLogSourceWizard
					collectorId={collectorId}
					definitionId={definitionId}
				/>
			</Suspense>
		</ErrorBoundary>
	)
}
